import React, { useState } from "react";
import {
  saveMessageAPI,
  sendMessageAPI,
} from "../../services/portal/admin/messageService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../utility/ToastMessage";
import { getMonthNameFromDate } from "../../utility/Common";

const MessageModal = ({ students, className, month, onClose }) => {
  const [smsMessage, setSmsMessage] = useState("");
  const [includeIndexNo, setIncludeIndexNo] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });

  const handleSendClick = () => {
    if (!smsMessage.trim()) {
      showErrorToastMsg("Please enter a message");
      return;
    }
    setShowConfirm(true);
  };

  const confirmSend = async () => {
    setSending(true);
    try {
      if (includeIndexNo) {
        // Send one by one with index number appended
        const total = students.length;
        setSendProgress({ current: 0, total });
        let failedCount = 0;

        for (let i = 0; i < students.length; i++) {
          const student = students[i];
          const personalizedMessage = `${smsMessage.trim()} \nYour index number is: ${student.IndexNo || ""}`;
          const params = {
            messageContent: personalizedMessage,
            messageTo: personalizedMessage,
            userList: String(student.UserId),
          };
          try {
            const res = await saveMessageAPI(params);
            if (res && res.IsSuccessful) {
              await sendMessageAPI(res.Data);
            } else {
              failedCount++;
            }
          } catch {
            failedCount++;
          }
          setSendProgress({ current: i + 1, total });
        }

        if (failedCount === 0) {
          showSuccessToastMsg("SMS sent successfully to all students");
        } else {
          showSuccessToastMsg(`SMS sent. ${failedCount} failed out of ${total}.`);
        }
        onClose();
      } else {
        // Batch send
        const result = students.map((s) => s.UserId).join(",");
        const params = {
          messageContent: smsMessage.trim(),
          messageTo: smsMessage.trim(),
          userList: result,
        };
        const res = await saveMessageAPI(params);
        if (res && res.IsSuccessful) {
          const sendRes = await sendMessageAPI(res.Data);
          if (sendRes && sendRes.IsSuccessful) {
            showSuccessToastMsg(sendRes.Message || "SMS sent successfully");
            onClose();
          } else {
            showErrorToastMsg(sendRes?.Message || "Failed to send SMS");
            setShowConfirm(false);
          }
        } else {
          showErrorToastMsg(res?.Message || "Failed to send SMS");
          setShowConfirm(false);
        }
      }
    } catch (error) {
      console.error("Error sending SMS:", error);
      showErrorToastMsg("Failed to send SMS");
      setShowConfirm(false);
    } finally {
      setSending(false);
      setSendProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-gray-900">Send SMS</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-2">
          {className} &mdash; {getMonthNameFromDate(month)} &mdash;{" "}
          <span className="font-medium text-gray-700">
            {students.length} student{students.length !== 1 ? "s" : ""}
          </span>
        </p>
        <div className="mb-4 max-h-28 overflow-y-auto border border-gray-200 rounded-md bg-gray-50 p-2">
          {students.map((s) => (
            <div key={s.UserId} className="text-xs text-gray-600 py-0.5">
              <span className="font-medium">{s.FirstName} {s.LastName}</span>
              {s.MobileNo && <span className="ml-2 text-gray-500">{s.MobileNo}</span>}
            </div>
          ))}
        </div>

        {!showConfirm ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={5}
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder="Type your message here..."
                className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">
                {smsMessage.length} characters
              </p>
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeIndexNo}
                  onChange={(e) => setIncludeIndexNo(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  Include the Index No. at the end of the SMS:{" "}
                  <span className="italic text-gray-500">Your index number is: ******</span>
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendClick}
                className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
              >
                Send SMS
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
              <p className="text-sm font-medium text-yellow-800 mb-1">
                Confirm before sending
              </p>
              <p className="text-sm text-yellow-700">
                You are about to send an SMS to{" "}
                <strong>{students.length}</strong> student
                {students.length !== 1 ? "s" : ""} in{" "}
                <strong>{className}</strong> for{" "}
                <strong>{getMonthNameFromDate(month)}</strong>.
              </p>
              <div className="mt-3 bg-white border border-yellow-100 rounded p-3 text-sm text-gray-700 whitespace-pre-wrap break-words">
                {smsMessage}
                <br />
                {includeIndexNo && (
                  <span className="text-gray-400"> Your index number is: ******</span>
                )}
              </div>
              {includeIndexNo && (
                <p className="text-xs text-yellow-700 mt-2">
                  Each student will receive their own index number appended to the message.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                disabled={sending}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                onClick={confirmSend}
                disabled={sending}
                className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {sending
                  ? includeIndexNo && sendProgress.total > 0
                    ? `Sending ${sendProgress.current}/${sendProgress.total}...`
                    : "Sending..."
                  : "Confirm & Send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MessageModal;
